import { motion } from "framer-motion";
import { useState } from "react";
import toast from "react-hot-toast";
import {
  FiArrowLeft,
  FiArrowRight,
  FiBookmark,
  FiCalendar,
  FiClock,
  FiLink,
  FiLinkedin,
  FiTag,
  FiTwitter,
  FiUser,
} from "react-icons/fi";
import { Link, useNavigate, useParams } from "react-router-dom";

/* ─── Blog Data ──────────────────────────────── */
const blogPosts = [
  {
    id: 1,
    slug: "best-design-tools-2024",
    title: "The 10 Best Design Tools of 2024",
    excerpt:
      "From wireframing to full prototyping, these design tools are reshaping how teams collaborate and build digital products.",
    author: "Sarah Williams",
    authorAvatar:
      "https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=100&h=100&fit=crop&crop=face",
    authorRole: "Head of Curation",
    date: "Jan 18, 2024",
    readTime: "6 min read",
    category: "Design",
    categoryColor: "#e64980",
    image:
      "https://images.unsplash.com/photo-1561070791-2526d30994b5?w=1200&h=600&fit=crop",
    tags: ["Design", "Tools", "UI/UX", "Figma"],
    content: `
The design industry has never had more powerful tools at its disposal. In 2024, the gap between design and development continues to shrink, with new platforms blurring the lines between ideation, prototyping, and production. Whether you're a solo designer or part of a large enterprise team, the right tool can be the difference between a good product and a great one.

## 1. Figma — The Collaborative Standard

Figma continues to dominate the UI/UX design space, and for good reason. Its real-time collaboration features make it the go-to choice for distributed teams. With the introduction of Dev Mode, designers can now hand off assets more seamlessly than ever before. The plugin ecosystem has exploded, offering everything from automatic accessibility checks to AI-powered design generation.

**Why it stands out:** Figma's browser-based approach means zero installation friction, and its free tier remains generous enough for most individual designers.

## 2. Framer — Motion-First Design

Framer has evolved from a prototyping tool into a full-fledged website builder. Its ability to create high-fidelity animations without writing code has made it a favorite for agencies and freelancers who want to impress clients with pixel-perfect, animated prototypes.

**Why it stands out:** The CMS integration allows designers to build and launch production-ready marketing sites without handing off to developers.

## 3. Canva — Design for Everyone

Canva's growth trajectory shows no signs of slowing. With 135 million monthly active users, it has democratized design for non-designers. The introduction of Magic Studio, their AI suite, has added text-to-image generation, background removal, and smart resize capabilities.

**Why it stands out:** The template library is unmatched, and the free tier offers more than most competitors.

## 4. Miro — Visual Collaboration

For workshops, brainstorming, and product planning, Miro remains the leading infinite canvas platform. Its integration with Jira, Asana, and Figma makes it a central hub for cross-functional teams.

**Why it stands out:** The quality of facilitation templates makes running remote workshops genuinely effective.

## 5. Adobe Creative Cloud — The Professional Suite

Despite competition from newer entrants, Adobe's suite remains essential for print, video, and advanced illustration work. Photoshop, Illustrator, and Premiere Pro are still the industry standards in their respective domains.

**Why it stands out:** The generative AI features in Photoshop (Firefly) are genuinely impressive for photo editing workflows.

## 6. Sketch — Mac-Native Excellence

While Sketch has ceded some ground to Figma, it remains a strong choice for Mac-only teams. The introduction of Sketch Prototyping and its offline-first approach still resonate with many designers who prefer native app performance.

## 7. Coolors — Color Palette Generation

For the specific task of building color systems, Coolors remains unbeatable. The ability to export palettes in every conceivable format — CSS variables, Sass, JSON, PDF — makes it a workflow essential.

## 8. Behance — Portfolio Showcase

As part of the Adobe ecosystem, Behance provides unparalleled reach for designers looking to showcase their portfolio. The integration with Adobe Portfolio makes publishing work straightforward.

## 9. Spline — 3D Design for the Web

3D design has historically required specialized tools with steep learning curves. Spline makes it accessible, allowing designers to create interactive 3D scenes that can be embedded directly in websites.

## 10. Penpot — Open Source Alternative

The open-source community has been watching Penpot closely. As a self-hostable alternative to Figma, it's gaining traction with teams that have data sovereignty requirements or simply prefer open-source tools.

## Conclusion

The design tooling landscape in 2024 is richer than ever. The key is not to use every tool, but to build a workflow with the right combination for your team's needs. For most teams, a core stack of Figma, Miro, and Canva covers the majority of design work effectively.

The emergence of AI-powered features across all these platforms signals where things are heading — expect 2025 to bring even more intelligent automation to the design process.
    `,
  },
  {
    id: 2,
    slug: "find-right-learning-platform",
    title: "How to Find the Right Learning Platform for You",
    excerpt:
      "With hundreds of e-learning options available, choosing the right platform can be overwhelming. Here is our comprehensive guide.",
    author: "Alex Johnson",
    authorAvatar:
      "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=100&h=100&fit=crop&crop=face",
    authorRole: "Founder & CEO",
    date: "Jan 12, 2024",
    readTime: "8 min read",
    category: "Education",
    categoryColor: "#40c057",
    image:
      "https://images.unsplash.com/photo-1501504905252-473c47e087f8?w=1200&h=600&fit=crop",
    tags: ["Education", "E-Learning", "Online Courses", "Career"],
    content: `
The online learning industry has exploded. From bite-sized YouTube tutorials to multi-year master's degrees, the options available to learners today would have seemed impossible just a decade ago. But this abundance of choice comes with its own challenge: how do you pick the right platform when there are hundreds competing for your attention and budget?

## Define Your Learning Goal First

Before evaluating any platform, you need to be crystal clear about what you're trying to achieve. Ask yourself:

- Are you looking to change careers, or deepen existing skills?
- Do you need a credential that employers recognize?
- How much time can you realistically commit per week?
- What's your learning style — video, interactive exercises, reading, or a mix?

Your answers will immediately eliminate most platforms and focus your search.

## Platform Categories Explained

### For Structured Academic Learning
**Coursera and edX** partner with universities to offer courses, certificates, and even full degrees. If you need an employer-recognized credential, these platforms offer the most legitimate pathways. Many courses can be audited for free, but certificates require payment.

### For Skill-Based Professional Development
**LinkedIn Learning and Udemy** excel at teaching specific, job-applicable skills. Udemy's vast library (200,000+ courses) makes it likely that whatever you need to learn, someone has created a course for it. LinkedIn Learning has the added advantage of displaying completed courses directly on your profile.

### For Creative Skills
**Skillshare** is purpose-built for creative learners. Its project-based approach — every class culminates in a real creative project — is particularly effective for skills like illustration, photography, video editing, and brand design.

### For Language Learning
**Duolingo** has essentially gamified language acquisition. Its streak system, short lessons, and daily reminders make it the most consistent tool available for language learners, though it works best as a supplement to more immersive practice.

### For Coding and Technical Skills
**Codecademy** offers the most structured path for complete beginners to programming. The in-browser coding environment means you're writing real code from lesson one, with no setup required.

## Key Questions to Evaluate Any Platform

**1. Is the content kept up to date?**
Especially in technology fields, outdated content can be actively harmful. Look for platforms that clearly display when content was last updated and have policies around refreshing courses.

**2. How is quality controlled?**
Anyone can publish on Udemy. Coursera content comes from vetted university partners. The quality range is dramatically different. Check reviews carefully on open platforms.

**3. What's the actual cost?**
Some platforms advertise "free" but gate the most valuable features behind paywalls. Calculate the real cost based on what you'll actually use.

**4. Does it match your learning style?**
Some people learn best from watching video lectures. Others need to get their hands dirty immediately. Platforms like Brilliant focus entirely on interactive problem-solving, which is fundamentally different from watching a video.

## Our Recommendation by Goal

| Goal | Platform |
|------|----------|
| Career change into tech | Coursera, edX |
| Learn a specific skill fast | Udemy |
| Creative skills | Skillshare |
| Learn a language | Duolingo |
| Code from scratch | Codecademy |
| General professional development | LinkedIn Learning |
| Deep mathematical thinking | Brilliant |

## The Most Common Mistake

The biggest mistake learners make is not finishing courses. Research consistently shows that most online course enrollments result in less than 10% completion. The best platform for you isn't the one with the best content — it's the one whose format and structure keeps you engaged enough to actually finish.

Before committing to a paid platform, use the free tier or take advantage of trial periods to test whether the learning format genuinely works for you.
    `,
  },
  {
    id: 3,
    slug: "developer-tools-workflow",
    title: "Developer Tools That Will Change Your Workflow",
    excerpt:
      "Productivity tools, code editors, and deployment platforms that every modern developer should know about.",
    author: "Mike Chen",
    authorAvatar:
      "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&h=100&fit=crop&crop=face",
    authorRole: "Lead Developer",
    date: "Jan 5, 2024",
    readTime: "5 min read",
    category: "Developer Tools",
    categoryColor: "#20c997",
    image:
      "https://images.unsplash.com/photo-1555066931-4365d14bab8c?w=1200&h=600&fit=crop",
    tags: ["Development", "Productivity", "Tools", "DevOps"],
    content: `
The best developers aren't necessarily the ones who know the most about any given language or framework. They're the ones who have optimized their workflow so thoroughly that they can move from idea to deployed product with minimal friction. The tools you choose have an enormous impact on this.

## Editor: VS Code with the Right Extensions

Visual Studio Code has won the editor wars, at least for now. But a vanilla VS Code installation is just the starting point. The extensions that matter most:

**GitHub Copilot** — AI code completion that goes far beyond simple autocomplete. It understands context and can write entire functions from a comment description. The productivity gains are real and measurable.

**Prettier** — Automatic code formatting eliminates style debates and keeps codebases consistent. Pair it with ESLint and you'll spend zero time arguing about semicolons.

**GitLens** — Shows you the full git history for any line of code, who wrote it, and why. Invaluable for understanding legacy codebases.

## Version Control: GitHub with Signed Commits

GitHub remains the standard, but many developers aren't using it to its full potential. Set up:

- **Branch protection rules** on main — require PR reviews and passing CI
- **Signed commits** with GPG — adds trust and prevents spoofing
- **GitHub Actions** — automate testing, linting, and deployment on every push

## API Development: Postman → Insomnia → Bruno

Postman is the market leader, but it's become bloated. Insomnia offers a cleaner interface. The newer entrant **Bruno** is worth watching — it stores API collections as plain files in your repository, making version control trivial.

## Deployment: Vercel + Railway

For frontend work, Vercel's zero-configuration deployments are hard to beat. For backend services that need persistent storage and databases, Railway has become a favorite for its simplicity and transparent pricing.

The combination of Vercel for frontend and Railway for backend covers most production workloads with minimal DevOps complexity.

## Local Development: Docker Compose

Stop installing databases and services directly on your machine. A docker-compose.yml file can spin up a complete local environment — Postgres, Redis, whatever you need — in seconds, and tear it down just as fast. This also ensures that everyone on the team is working with identical dependencies.

## Monitoring: Sentry + Uptime Robot

**Sentry** catches errors in production before your users report them. The amount of time it saves in debugging is staggering — it captures the full stack trace, the user's browser, the URL, and often the exact line of code that failed.

**Uptime Robot** (free tier) monitors your endpoints and sends alerts if they go down. Takes five minutes to set up and has saved more than one on-call weekend.

## Database Tooling: TablePlus

For actually looking at and manipulating database content, TablePlus has the best interface available. Supports PostgreSQL, MySQL, SQLite, Redis, and more through a single, native application.

## The Single Most Impactful Change

If you're going to take one thing from this list, make it **automating your deployment pipeline**. Setting up GitHub Actions to run your tests and deploy on merge to main takes a few hours to configure but saves hours every week and dramatically reduces the risk of human error in deployments.

The time you invest in optimizing your tooling pays compound dividends for the rest of your career.
    `,
  },
  {
    id: 4,
    slug: "rise-of-no-code-platforms",
    title: "The Rise of No-Code Platforms",
    excerpt:
      "No-code tools are democratizing software creation. We explore the best platforms available today.",
    author: "Emma Davis",
    authorAvatar:
      "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=100&h=100&fit=crop&crop=face",
    authorRole: "Community Manager",
    date: "Dec 28, 2023",
    readTime: "7 min read",
    category: "Technology",
    categoryColor: "#4c6ef5",
    image:
      "https://images.unsplash.com/photo-1519389950473-47ba0277781c?w=1200&h=600&fit=crop",
    tags: ["No-Code", "Technology", "Startups", "Tools"],
    content: `
Just five years ago, building software required either hiring developers or learning to code yourself. Today, a new generation of no-code platforms has made it possible for founders, marketers, and operations teams to build functional, production-ready applications without writing a single line of code.

## What Is No-Code, Really?

No-code platforms provide visual interfaces that abstract away the underlying programming. Instead of writing if statements and for loops, you drag and drop components, configure logic through visual interfaces, and connect services through pre-built integrations.

The category spans a wide range of tools, from simple form builders to sophisticated application platforms capable of replacing custom software.

## The Leading Platforms

### Bubble — Full Stack Without Code

Bubble is the most powerful no-code platform for building web applications. It handles the database, frontend, and backend logic through a single visual interface. Marketplaces, SaaS products, and internal tools built on Bubble regularly reach thousands of users.

The tradeoff is a steep learning curve by no-code standards. Bubble's visual programming model requires you to think like a developer even if you're not writing code.

### Webflow — Design Freedom for Websites

Webflow occupies the space between website builders and custom development. Unlike Squarespace or Wix, Webflow generates clean, semantic HTML and CSS. Designers with no coding experience can build pixel-perfect, animated websites with complex interactions.

The CMS and e-commerce features have made it a legitimate alternative to WordPress for marketing teams.

### Zapier and Make — Automation Platforms

Zapier pioneered the workflow automation space, and Make (formerly Integromat) has become a powerful alternative with more complex logic capabilities. These platforms connect thousands of apps and automate repetitive tasks.

The no-code automation market has driven significant productivity gains in operations teams, marketing departments, and customer success organizations.

### Airtable — Database Without SQL

Airtable made databases accessible to non-technical users by combining the familiarity of spreadsheets with the power of relational databases. Its API and automation features have made it a popular choice for operations and product teams.

### Glide — Mobile Apps from Spreadsheets

Glide can turn a Google Sheet into a mobile application in minutes. It's genuinely remarkable technology, and the use cases are surprisingly broad — everything from team directories to field service apps.

## What No-Code Can and Cannot Do

No-code platforms are excellent for:
- MVPs and rapid prototyping
- Internal tools and automations
- Websites and landing pages
- Simple to medium-complexity applications

They struggle with:
- Complex algorithmic logic
- High-performance requirements
- Unusual or highly specific functionality
- Enterprise-scale applications

## The Honest Assessment

The most valuable thing no-code platforms provide isn't the elimination of developers — it's the ability to validate ideas and solve problems without the full cost and timeline of custom development. Many successful products have started as no-code builds and only transitioned to custom development when scale required it.

For individuals, no-code skills are increasingly valuable. The ability to build and automate without developer support is a genuine career differentiator.
    `,
  },
  {
    id: 5,
    slug: "best-websites-remote-workers",
    title: "Best Websites for Remote Workers in 2024",
    excerpt:
      "Working from anywhere requires the right digital toolkit. These websites will help you stay productive and connected.",
    author: "Sarah Williams",
    authorAvatar:
      "https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=100&h=100&fit=crop&crop=face",
    authorRole: "Head of Curation",
    date: "Dec 20, 2023",
    readTime: "6 min read",
    category: "Productivity",
    categoryColor: "#7950f2",
    image:
      "https://images.unsplash.com/photo-1484480974693-6ca0a78fb36b?w=1200&h=600&fit=crop",
    tags: ["Remote Work", "Productivity", "Tools", "Lifestyle"],
    content: `
Remote work has permanently reshaped how millions of professionals spend their days. The home office, the café, the co-working space — the "office" now means wherever you open your laptop. With this freedom comes a new set of challenges: maintaining focus, collaborating asynchronously, and separating work from personal life.

The websites and tools you use daily have an outsized impact on how successfully you navigate these challenges. Here are the ones we recommend.

## Communication and Collaboration

**Slack** remains the standard for team communication. The key to using Slack productively as a remote worker is discipline: set notification schedules, use threads to keep channels organized, and resist the urge to be always-available.

**Notion** has become the default for distributed team documentation. Its flexibility allows teams to build everything from meeting notes to project wikis to personal task lists in a single tool.

**Loom** is underrated. Being able to record a quick video message — your face, your screen, or both — eliminates entire email chains. Asynchronous video communication is faster and more personal than text for complex explanations.

## Focus and Time Management

**Toggl** is the gold standard for time tracking. Whether you're billing clients or simply trying to understand how your time is actually spent, Toggl's simplicity makes it sustainable to use daily.

**Forest** takes an unusual approach to focus: you plant a virtual tree when you start a focus session, and the tree dies if you leave the app. The gamification works surprisingly well and the app has actual reforestation partnerships.

**RescueTime** runs in the background and automatically categorizes how you spend your computer time. The weekly reports can be humbling — and productive.

## Finding Work and Clients

**LinkedIn** is essential for visibility as a remote worker or freelancer. Regular posts, thoughtful comments, and a complete profile significantly increase inbound opportunities.

**Upwork** and **Contra** serve different ends of the freelance market. Upwork is better for volume work and finding initial clients; Contra is building toward the premium end of freelancing with no platform fees.

## Health and Wellbeing

Remote work can blur the boundaries between work and personal time in damaging ways. Build regular practices using:

**Headspace or Calm** — Even 10 minutes of daily meditation meaningfully reduces stress. Both apps have workday-specific content.

**Strava** — Logging physical activity and connecting with friends creates accountability for exercise, which remote workers often neglect.

## Managing Finances

**Wise** is essential for remote workers getting paid across borders. The real exchange rates and low fees save significant money compared to bank wire transfers.

**Wave** provides free accounting software that handles invoicing, receipts, and financial reporting — sufficient for most freelancers and small remote businesses.

## The Most Important Investment

None of these tools matter if your physical setup is poor. Invest in a good chair, an external monitor, and reliable internet before any software subscription. Your hardware and physical environment are the foundation everything else is built on.
    `,
  },
  {
    id: 6,
    slug: "top-finance-apps-young-investors",
    title: "Top Finance Apps for Young Investors",
    excerpt:
      "From commission-free trading to automated savings, here are the fintech platforms making money management simple.",
    author: "Alex Johnson",
    authorAvatar:
      "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=100&h=100&fit=crop&crop=face",
    authorRole: "Founder & CEO",
    date: "Dec 14, 2023",
    readTime: "5 min read",
    category: "Finance",
    categoryColor: "#12b886",
    image:
      "https://images.unsplash.com/photo-1563013544-824ae1b704d3?w=1200&h=600&fit=crop",
    tags: ["Finance", "Investing", "Personal Finance", "Fintech"],
    content: `
The past decade has seen a dramatic democratization of investing. What once required a broker, a minimum investment of thousands of dollars, and commission fees on every trade is now accessible to anyone with a smartphone and a few dollars to invest.

For young investors in particular, the fintech revolution has created genuinely good options across every aspect of personal finance — from tracking spending to building long-term wealth.

## Starting with Budgeting

Before investing a single dollar, understand where your money is going. **Mint** (now Intuit Mint) has long been the standard for budget tracking — it automatically imports transactions from your bank accounts and credit cards and categorizes them. The free dashboard gives you a clear picture of your spending patterns.

**YNAB (You Need A Budget)** takes a more proactive approach. Rather than tracking past spending, it asks you to assign every dollar a "job" before you spend it. The methodology has a devoted following and genuinely changes spending behavior for those who commit to it.

## Commission-Free Investing

**Robinhood** pioneered commission-free trading and forced the entire industry to follow. Today, most major brokerages offer zero-commission trades. Robinhood remains popular for its clean interface and fractional share investing, which lets you buy a portion of expensive stocks like Amazon or Google with as little as one dollar.

For more serious investors, **Fidelity** or **Charles Schwab** offer everything Robinhood does plus superior research tools, customer service, and retirement accounts.

## Automated Investing

**Wealthfront** and **Betterment** are robo-advisors — they automatically invest your money in a diversified portfolio of ETFs based on your risk tolerance and rebalance as needed. Both offer tax-loss harvesting, which can meaningfully improve after-tax returns.

For complete automation with minimal decision-making, robo-advisors are excellent. The fees (typically 0.25% annually) are well worth the convenience for most people.

## Cryptocurrency

**Coinbase** remains the most accessible entry point to cryptocurrency for new investors. The interface is clean, the assets are insured, and the educational content helps new users understand what they're buying.

That said — approach crypto with appropriate caution. It remains highly speculative, and allocations above 5-10% of a portfolio are difficult to justify for most investors.

## International Transfers

For anyone receiving income from international clients or sending money abroad, **Wise** is transformative. Using the real mid-market exchange rate with transparent fees, it typically saves 70-80% compared to bank wire transfers.

## Building Good Habits Early

The most important principle for young investors isn't which app to use — it's starting early and investing consistently. A 22-year-old who invests $200 per month will dramatically outperform a 32-year-old who invests $400 per month, simply due to compound interest.

Automate your investments so they happen without conscious decision-making, diversify across asset classes, and resist the temptation to react to short-term market movements. These principles matter far more than any specific platform.
    `,
  },
];

/* ─── Render Markdown-like Content ──────────── */
const renderContent = (content) => {
  const lines = content.trim().split("\n");
  const elements = [];
  let i = 0;
  let keyCounter = 0;

  while (i < lines.length) {
    const line = lines[i];

    // H2
    if (line.startsWith("## ")) {
      elements.push(
        <h2
          key={`h2-${keyCounter++}`}
          className="text-xl sm:text-2xl font-extrabold text-slate-900 mt-10 mb-4 font-display"
        >
          {line.replace("## ", "")}
        </h2>,
      );
      i++;
    }
    // H3
    else if (line.startsWith("### ")) {
      elements.push(
        <h3
          key={`h3-${keyCounter++}`}
          className="text-lg font-extrabold text-slate-800 mt-7 mb-3"
        >
          {line.replace("### ", "")}
        </h3>,
      );
      i++;
    }
    // Table
    else if (line.startsWith("| ")) {
      const tableStart = i;
      const tableLines = [];
      while (i < lines.length && lines[i].startsWith("|")) {
        tableLines.push(lines[i]);
        i++;
      }
      const header = tableLines[0].split("|").filter((c) => c.trim());
      const rows = tableLines
        .slice(2)
        .map((r) => r.split("|").filter((c) => c.trim()));
      elements.push(
        <div key={`table-${keyCounter++}`} className="overflow-x-auto my-6">
          <table className="w-full border-collapse border-2 border-slate-200 rounded-xl overflow-hidden text-sm">
            <thead>
              <tr className="bg-slate-50">
                {header.map((h, hi) => (
                  <th
                    key={hi}
                    className="border border-slate-200 px-4 py-3 text-left font-extrabold text-slate-800"
                  >
                    {h.trim()}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, ri) => (
                <tr
                  key={ri}
                  className={ri % 2 === 0 ? "bg-white" : "bg-slate-50/50"}
                >
                  {row.map((cell, ci) => (
                    <td
                      key={ci}
                      className="border border-slate-200 px-4 py-3 text-slate-700"
                    >
                      {cell.trim()}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>,
      );
      // Don't increment i — already advanced past the table
    }
    // Unordered list
    else if (line.startsWith("- ")) {
      const items = [];
      while (i < lines.length && lines[i].startsWith("- ")) {
        items.push(lines[i].replace("- ", ""));
        i++;
      }
      elements.push(
        <ul key={`ul-${keyCounter++}`} className="my-4 space-y-2">
          {items.map((item, ii) => (
            <li
              key={ii}
              className="flex items-start gap-2.5 text-slate-700 text-[15px] leading-relaxed"
            >
              <span className="w-1.5 h-1.5 bg-indigo-400 rounded-full mt-2 flex-shrink-0" />
              {renderInline(item)}
            </li>
          ))}
        </ul>,
      );
      // Don't increment i — already advanced past the list
    }
    // Bold paragraph (starts with **)
    else if (line.startsWith("**") && line.includes("**")) {
      elements.push(
        <p
          key={`bold-${keyCounter++}`}
          className="text-[15px] text-slate-700 leading-relaxed mb-3"
        >
          {renderInline(line)}
        </p>,
      );
      i++;
    }
    // Empty line
    else if (line.trim() === "") {
      i++;
    }
    // Regular paragraph
    else {
      elements.push(
        <p
          key={`p-${keyCounter++}`}
          className="text-[15px] text-slate-700 leading-[1.8] mb-4"
        >
          {renderInline(line)}
        </p>,
      );
      i++;
    }
  }

  return elements;
};

/* ─── Inline formatting ──────────────────────── */
const renderInline = (text) => {
  const parts = [];
  const regex = /\*\*(.*?)\*\*/g;
  let last = 0;
  let match;
  let matchIndex = 0;

  while ((match = regex.exec(text)) !== null) {
    if (match.index > last) {
      parts.push(
        <span key={`text-${matchIndex}`}>{text.slice(last, match.index)}</span>,
      );
    }
    parts.push(
      <strong
        key={`bold-${matchIndex}`}
        className="font-extrabold text-slate-900"
      >
        {match[1]}
      </strong>,
    );
    matchIndex++;
    last = match.index + match[0].length;
  }

  if (last < text.length) {
    if (parts.length > 0) {
      parts.push(<span key={`text-end`}>{text.slice(last)}</span>);
    } else {
      return text;
    }
  }

  return parts.length > 0 ? parts : text;
};

/* ─── Blog Post Page ─────────────────────────── */
const BlogPostPage = ({ post }) => {
  const navigate = useNavigate();
  const [bookmarked, setBookmarked] = useState(false);
  const otherPosts = blogPosts.filter((p) => p.id !== post.id).slice(0, 3);

  const handleShare = (type) => {
    if (type === "link") {
      navigator.clipboard.writeText(window.location.href);
      toast.success("Link copied!");
    } else if (type === "twitter") {
      window.open(
        `https://twitter.com/intent/tweet?text=${encodeURIComponent(post.title)}&url=${encodeURIComponent(window.location.href)}`,
        "_blank",
      );
    } else if (type === "linkedin") {
      window.open(
        `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(window.location.href)}`,
        "_blank",
      );
    }
  };

  return (
    <div className="section-tinted min-h-screen">
      <div className="container-main py-8 lg:py-12">
        {/* Back button */}
        <motion.button
          initial={{ opacity: 0, x: -10 }}
          animate={{ opacity: 1, x: 0 }}
          onClick={() => navigate("/blog")}
          className="btn-ghost mb-6 -ml-2"
        >
          <FiArrowLeft size={15} /> Back to Blog
        </motion.button>

        <div className="grid lg:grid-cols-4 gap-8 lg:gap-12 items-start">
          {/* ── Article ── */}
          <motion.article
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="lg:col-span-3"
          >
            {/* Cover image */}
            <div className="rounded-2xl overflow-hidden mb-8 shadow-lg">
              <img
                src={post.image}
                alt={post.title}
                className="w-full h-56 sm:h-72 lg:h-80 object-cover"
              />
            </div>

            {/* Meta */}
            <div className="flex flex-wrap items-center gap-3 mb-5">
              <span
                className="badge text-[11px] font-bold text-white"
                style={{
                  backgroundColor: post.categoryColor,
                  borderColor: post.categoryColor,
                }}
              >
                {post.category}
              </span>
              <div className="flex items-center gap-1.5 text-sm font-semibold text-slate-400">
                <FiCalendar size={13} /> {post.date}
              </div>
              <div className="flex items-center gap-1.5 text-sm font-semibold text-slate-400">
                <FiClock size={13} /> {post.readTime}
              </div>
            </div>

            {/* Title */}
            <h1 className="page-title mb-5 leading-tight">{post.title}</h1>

            {/* Excerpt */}
            <p className="text-lg text-slate-500 leading-relaxed mb-8 pb-8 border-b-2 border-slate-100">
              {post.excerpt}
            </p>

            {/* Author */}
            <div className="flex items-center gap-3 mb-10">
              <img
                src={post.authorAvatar}
                alt={post.author}
                className="w-11 h-11 rounded-full object-cover ring-2 ring-slate-200"
              />
              <div>
                <p className="text-sm font-extrabold text-slate-900">
                  {post.author}
                </p>
                <p className="text-xs font-semibold text-slate-500">
                  {post.authorRole}
                </p>
              </div>
            </div>

            {/* Content */}
            <div className="prose-content">{renderContent(post.content)}</div>

            {/* Tags */}
            <div className="flex flex-wrap gap-2 mt-10 pt-8 border-t-2 border-slate-100">
              {post.tags.map((tag) => (
                <span
                  key={tag}
                  className="inline-flex items-center gap-1.5 badge-slate text-[11px]"
                >
                  <FiTag size={9} /> {tag}
                </span>
              ))}
            </div>

            {/* Share */}
            <div className="flex flex-wrap items-center gap-3 mt-6 pt-6 border-t-2 border-slate-100">
              <span className="text-sm font-bold text-slate-700">Share:</span>
              <button
                onClick={() => handleShare("twitter")}
                className="btn-secondary !py-2 !px-3 !text-xs gap-1.5"
              >
                <FiTwitter size={14} /> Twitter
              </button>
              <button
                onClick={() => handleShare("linkedin")}
                className="btn-secondary !py-2 !px-3 !text-xs gap-1.5"
              >
                <FiLinkedin size={14} /> LinkedIn
              </button>
              <button
                onClick={() => handleShare("link")}
                className="btn-secondary !py-2 !px-3 !text-xs gap-1.5"
              >
                <FiLink size={14} /> Copy Link
              </button>
              <button
                onClick={() => setBookmarked(!bookmarked)}
                className={`btn-secondary !py-2 !px-3 !text-xs gap-1.5 ml-auto ${bookmarked ? "!bg-indigo-50 !text-indigo-600 !border-indigo-200" : ""}`}
              >
                <FiBookmark size={14} />
                {bookmarked ? "Saved" : "Save"}
              </button>
            </div>
          </motion.article>

          {/* ── Sidebar ── */}
          <aside className="space-y-5">
            <div className="lg:sticky lg:top-[4.5rem] space-y-5">
              {/* Author card */}
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.1 }}
                className="card p-5"
              >
                <h3 className="text-xs font-extrabold uppercase tracking-widest text-slate-400 mb-4">
                  Written by
                </h3>
                <div className="flex items-center gap-3 mb-3">
                  <img
                    src={post.authorAvatar}
                    alt={post.author}
                    className="w-12 h-12 rounded-xl object-cover ring-2 ring-slate-100"
                  />
                  <div>
                    <p className="font-extrabold text-slate-900 text-sm">
                      {post.author}
                    </p>
                    <p className="text-xs font-semibold text-slate-500">
                      {post.authorRole}
                    </p>
                  </div>
                </div>
                <p className="text-xs text-slate-500 leading-relaxed">
                  Part of the SiteVault editorial team, curating the best of the
                  web since 2022.
                </p>
              </motion.div>

              {/* More articles */}
              {otherPosts.length > 0 && (
                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.15 }}
                  className="card p-5"
                >
                  <h3 className="text-xs font-extrabold uppercase tracking-widest text-slate-400 mb-4">
                    More Articles
                  </h3>
                  <div className="space-y-4">
                    {otherPosts.map((p) => (
                      <Link
                        key={p.id}
                        to={`/blog/${p.slug}`}
                        className="block group"
                      >
                        <div className="flex gap-3">
                          <img
                            src={p.image}
                            alt={p.title}
                            className="w-14 h-14 rounded-lg object-cover flex-shrink-0 ring-1 ring-slate-100"
                          />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-bold text-slate-800 group-hover:text-indigo-600 transition-colors leading-snug line-clamp-2">
                              {p.title}
                            </p>
                            <p className="text-[10px] font-semibold text-slate-400 mt-1">
                              {p.readTime}
                            </p>
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>
                  <Link
                    to="/blog"
                    className="btn-ghost w-full mt-4 text-xs !text-indigo-600"
                  >
                    View all articles <FiArrowRight size={12} />
                  </Link>
                </motion.div>
              )}

              {/* Back to directory */}
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.2 }}
                className="card p-5 bg-gradient-to-br from-indigo-50 to-violet-50 border-2 border-indigo-100"
              >
                <h3 className="text-sm font-extrabold text-slate-900 mb-1">
                  Discover great websites
                </h3>
                <p className="text-xs text-slate-600 leading-relaxed mb-4">
                  Browse our curated directory of 1,800+ verified websites.
                </p>
                <Link
                  to="/websites"
                  className="btn-primary w-full !py-2 !text-xs"
                >
                  Browse Directory <FiArrowRight size={12} />
                </Link>
              </motion.div>
            </div>
          </aside>
        </div>

        {/* Related posts */}
        {otherPosts.length > 0 && (
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mt-16 pt-12 border-t-2 border-slate-100"
          >
            <h2 className="section-title mb-8">More from the Blog</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {otherPosts.map((p, idx) => (
                <motion.div
                  key={p.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.06 }}
                >
                  <Link
                    to={`/blog/${p.slug}`}
                    className="card-hover block group overflow-hidden h-full"
                  >
                    <div className="h-36 overflow-hidden">
                      <img
                        src={p.image}
                        alt={p.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                      />
                    </div>
                    <div className="p-4">
                      <span
                        className="badge text-[9px] font-bold text-white mb-2"
                        style={{
                          backgroundColor: p.categoryColor,
                          borderColor: p.categoryColor,
                        }}
                      >
                        {p.category}
                      </span>
                      <h3 className="font-extrabold text-slate-900 group-hover:text-indigo-600 transition-colors text-sm leading-snug mb-2">
                        {p.title}
                      </h3>
                      <div className="flex items-center gap-3 text-[11px] font-semibold text-slate-400">
                        <span>{p.date}</span>
                        <span>·</span>
                        <span>{p.readTime}</span>
                      </div>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          </motion.section>
        )}
      </div>
    </div>
  );
};

/* ─── Blog List Page ─────────────────────────── */
const BlogListPage = () => (
  <div className="section-tinted min-h-screen">
    <div className="container-main py-10 lg:py-14">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-12"
      >
        <div className="accent-line" />
        <p className="label-tag mb-2">Our Blog</p>
        <h1 className="page-title">Insights & Guides</h1>
        <p className="section-subtitle max-w-xl">
          Expert guides, curated lists, and tips to help you discover and get
          the most out of the web.
        </p>
      </motion.div>

      {/* Featured */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.05 }}
        className="card-hover overflow-hidden mb-8"
      >
        <Link
          to={`/blog/${blogPosts[0].slug}`}
          className="grid lg:grid-cols-2 group"
        >
          <div className="relative h-64 lg:h-auto overflow-hidden">
            <img
              src={blogPosts[0].image}
              alt={blogPosts[0].title}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
            />
            <div className="absolute top-4 left-4">
              <span
                className="badge text-[10px] font-bold text-white"
                style={{
                  backgroundColor: blogPosts[0].categoryColor,
                  borderColor: "transparent",
                }}
              >
                {blogPosts[0].category}
              </span>
            </div>
          </div>
          <div className="p-8 lg:p-10 flex flex-col justify-center">
            <span className="badge-blue mb-4 w-fit text-[10px] font-bold">
              Featured
            </span>
            <h2 className="text-2xl font-extrabold text-slate-900 group-hover:text-indigo-600 transition-colors mb-3 leading-tight">
              {blogPosts[0].title}
            </h2>
            <p className="text-slate-500 leading-relaxed mb-6 text-sm">
              {blogPosts[0].excerpt}
            </p>
            <div className="flex items-center gap-4 text-xs text-slate-400 font-semibold mb-6">
              <span className="flex items-center gap-1.5">
                <FiUser size={12} /> {blogPosts[0].author}
              </span>
              <span className="flex items-center gap-1.5">
                <FiCalendar size={12} /> {blogPosts[0].date}
              </span>
              <span className="flex items-center gap-1.5">
                <FiClock size={12} /> {blogPosts[0].readTime}
              </span>
            </div>
            <span className="btn-primary w-fit text-sm">
              Read Article <FiArrowRight size={14} />
            </span>
          </div>
        </Link>
      </motion.div>

      {/* Grid */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {blogPosts.slice(1).map((post, idx) => (
          <motion.div
            key={post.id}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: idx * 0.06 }}
          >
            <Link
              to={`/blog/${post.slug}`}
              className="card-hover block group overflow-hidden h-full"
            >
              <div className="relative h-44 overflow-hidden">
                <img
                  src={post.image}
                  alt={post.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                />
                <div className="absolute top-3 left-3">
                  <span
                    className="badge text-[9px] font-bold text-white"
                    style={{
                      backgroundColor: post.categoryColor,
                      borderColor: "transparent",
                    }}
                  >
                    {post.category}
                  </span>
                </div>
              </div>
              <div className="p-5">
                <h3 className="font-extrabold text-slate-900 group-hover:text-indigo-600 transition-colors mb-2 leading-tight">
                  {post.title}
                </h3>
                <p className="text-sm text-slate-500 line-clamp-2 leading-relaxed mb-4">
                  {post.excerpt}
                </p>
                <div className="flex items-center justify-between pt-3 border-t-2 border-slate-100">
                  <div className="flex items-center gap-2">
                    <img
                      src={post.authorAvatar}
                      alt={post.author}
                      className="w-6 h-6 rounded-full object-cover"
                    />
                    <span className="text-[11px] font-bold text-slate-600">
                      {post.author}
                    </span>
                  </div>
                  <div className="flex items-center gap-1.5 text-[10px] font-semibold text-slate-400">
                    <FiClock size={10} /> {post.readTime}
                  </div>
                </div>
              </div>
            </Link>
          </motion.div>
        ))}
      </div>
    </div>
  </div>
);

/* ─── Router wrapper ─────────────────────────── */
const BlogPage = () => {
  const { slug } = useParams();
  const post = slug ? blogPosts.find((p) => p.slug === slug) : null;

  if (slug && !post) {
    return (
      <div className="container-main py-20 text-center">
        <h2 className="text-xl font-extrabold text-slate-900 mb-2">
          Article not found
        </h2>
        <p className="text-slate-500 mb-6">
          The article you're looking for doesn't exist.
        </p>
        <Link to="/blog" className="btn-primary">
          <FiArrowLeft size={14} /> Back to Blog
        </Link>
      </div>
    );
  }

  return post ? <BlogPostPage post={post} /> : <BlogListPage />;
};

export default BlogPage;
